// The selector (AppIconChipToggle / AppToggleButton, role="group") whose buttons read exactly these labels
function findToggle(...labels) {
	const want = labels.map(l => l.toLowerCase())
	return [...document.querySelectorAll('[role="group"]')].find(group => {
		const names = [...group.querySelectorAll('button')].map(b => b.textContent.trim().toLowerCase())
		return want.every(w => names.includes(w))
	}) || null
}

// The left group of toggles of the filter panel (the first ancestor with more than one selector)
function findTogglesRow() {
	let el = findToggle('Total price', 'Daily')
	while (el && el.querySelectorAll('[role="group"]').length < 2) el = el.parentElement
	return el
}

// A filter field: its cell in the filters grid, found by its exact label
function findField(...texts) {
	const want = texts.map(t => t.toLowerCase())
	const label = [...document.querySelectorAll('label')].find(l => want.includes(l.textContent.trim().toLowerCase()))
	return label?.closest('.MuiGrid-item') || label?.closest('.MuiFormControl-root') || null
}

// The CSS grid that lays out the filter fields
function findFieldsGrid() {
	let el = findField('Providers')
	while (el && getComputedStyle(el).display !== 'grid') el = el.parentElement
	return el
}

// The header iconbuttons + saved filters selector, next to the Virtual Tour button
function findShareActions() {
	let el = document.querySelector('[data-icon="floppy-disk"], .fa-floppy-disk')?.closest('button')
	while (el && !el.querySelector('.MuiAutocomplete-root')) el = el.parentElement
	return el
}

// The results card (real table or demo), by the exact text of its AppCardTitle
function findTableCard() {
	const el = [...document.querySelectorAll('span.MuiTypography-root')].find(e => e.textContent.trim() === 'Leading Price Table')
	return el?.closest('.MuiPaper-root') || null
}

// One box over several elements, for a step that talks about a column or a row of cells
function unionTarget(els) {
	const list = els.filter(Boolean)
	if (!list.length) return null
	return {
		scrollIntoView: opts => list[Math.floor(list.length / 2)].scrollIntoView(opts),
		getBoundingClientRect: () => {
			const rects = list.map(e => e.getBoundingClientRect())
			const left = Math.min(...rects.map(r => r.left)), top = Math.min(...rects.map(r => r.top))
			const right = Math.max(...rects.map(r => r.right)), bottom = Math.max(...rects.map(r => r.bottom))
			return { left, top, right, bottom, width: right - left, height: bottom - top }
		},
	}
}

// Header cells and body rows of the results table, with the index of the "Office" column
function tableParts() {
	const table = findTableCard()?.querySelector('table')
	if (!table) return null
	const heads = [...table.querySelectorAll('thead th')]
	const officeIdx = heads.findIndex(th => th.textContent.trim() === 'Office')
	const rows = [...table.querySelectorAll('tbody tr')].filter(r => r.cells.length > officeIdx + 1)
	if (officeIdx < 0 || !rows.length) return null
	return { heads, officeIdx, rows }
}

// The "Office" column: its header and the office name of every row
function findOfficeColumn() {
	const parts = tableParts()
	return parts && unionTarget([parts.heads[parts.officeIdx], ...parts.rows.map(r => r.cells[parts.officeIdx])])
}

// The price cells of the first office (every date or period)
function findFirstRowPrices() {
	const parts = tableParts()
	return parts && unionTarget([...parts.rows[0].cells].slice(parts.officeIdx + 1))
}

// The engine measures the target 250 ms after a smooth scroll starts, too early for a long scroll (filters at
// the top, table at the bottom): every target here jumps into view at once, so the spotlight lands on it.
function instant(el) {
	return el && {
		scrollIntoView: () => el.scrollIntoView({ block: 'center' }),
		getBoundingClientRect: () => el.getBoundingClientRect(),
	}
}

const ES = navigator.language?.startsWith('es')

const STEPS = [
	{
		type: 'intro',
		icon: 'chart-mixed',
		title: ES ? 'Bienvenido a Precios Líderes' : 'Welcome to Leading Prices',
		description: ES
			? 'El Analizador de Precios Líderes compara tus precios con los de la competencia en diferentes oficinas y periodos de tiempo. Configura los filtros, selecciona oficinas y proveedores, y comprueba de un vistazo dónde eres más barato, más caro o estás igualado.'
			: 'The Leading Prices Analyzer compares your prices against competitors across offices and time periods. Configure filters, select offices and providers, then see at a glance where you are cheaper, more expensive, or equal.',
	},
	{
		type: 'field',
		findTarget: () => findShareActions(),
		icon: 'link',
		title: ES ? 'Compartir y Guardar Filtros' : 'Share & Save Filters',
		description: ES
			? 'Copia un enlace con todos los filtros actuales, guarda la configuración con un nombre para más tarde, o carga un conjunto de filtros guardado desde el desplegable.'
			: 'Copy a link with all the current filters, save the configuration with a name for later, or load a saved filter set from the dropdown.',
	},
	{
		type: 'field',
		findTarget: () => findTogglesRow(),
		icon: 'toggle-on',
		title: ES ? 'Selectores de Filtro' : 'Filter Toggles',
		description: ES
			? 'Elige precio total o diario, oficinas sueltas o un grupo de oficinas, sitios sueltos o un grupo de sitios, y si comparas contra un sitio principal o un proveedor principal.'
			: 'Pick total or daily price, single offices or an office group, single sites or a site group, and whether you compare against a main site or a main provider.',
		example: '"Daily" | "Office group" | "Site group" | "Main Site" / "Main Provider"',
	},
	{
		type: 'field',
		findTarget: () => findFieldsGrid(),
		icon: 'filter',
		title: ES ? 'Filtros de Búsqueda' : 'Search Filters',
		description: ES
			? 'Configura tu análisis: selecciona un sitio principal o un proveedor principal (obligatorio), oficinas de recogida, sitios, rangos de fechas de reserva y recogida, agrupación por día/semana/mes, categorías, LOR, niveles, proveedores y moneda. Cambia el selector de la derecha a «Show features» para ACRISS, kilometraje, política de combustible y más.'
			: 'Configure your analysis: select a main site or a main provider (required), pick up offices, sites, booking and pickup date ranges, group by day/week/month, categories, LOR, tiers, providers, and currency. Switch the toggle on the right to "Show features" for ACRISS, mileage, fuel policy, and more.',
	},
	{
		type: 'field',
		findTarget: () => findToggle('Min. Price', 'Avg. Price', 'Max. Price'),
		skipIf: () => !findToggle('Min. Price', 'Avg. Price', 'Max. Price'),
		icon: 'sliders',
		title: ES ? 'Agregación de Precios' : 'Price Aggregation',
		description: ES
			? 'Alterna entre precios Mínimo, Medio y Máximo. Esto cambia el precio mostrado en cada celda de la tabla. Mínimo está seleccionado por defecto.'
			: 'Toggle between Min, Average, and Max prices. This changes the price shown in every cell of the table. Min is selected by default.',
		example: '"Min. Price" | "Avg. Price" | "Max. Price"',
	},
	{
		type: 'field',
		findTarget: () => findTableCard(),
		skipIf: () => !findTableCard(),
		icon: 'table-cells',
		title: ES ? 'Tabla de Precios Líderes' : 'Leading Price Table',
		description: ES
			? 'La tabla de resultados. La primera columna muestra los nombres de las oficinas (fija a la izquierda). Las columnas siguientes representan fechas o periodos de tiempo según tu selección de Agrupar Por (día, semana o mes). Cada celda compara el precio principal con el del competidor líder.'
			: 'The results table. The first column shows office names (sticky on the left). Subsequent columns represent dates or time periods depending on your Group By selection (day, week, or month). Each cell compares the main price against the leading competitor.',
	},
	{
		type: 'field',
		findTarget: () => findOfficeColumn(),
		skipIf: () => !findOfficeColumn(),
		icon: 'building',
		title: ES ? 'Columna de Oficina' : 'Office Column',
		description: ES
			? 'La primera columna muestra los nombres de las oficinas en negrita. Esta columna permanece fija a la izquierda al desplazarte horizontalmente, para que siempre sepas qué oficina estás consultando.'
			: 'The first column displays office names in bold. This column stays fixed on the left when scrolling horizontally, so you always know which office you are looking at.',
	},
	{
		type: 'field',
		findTarget: () => findFirstRowPrices(),
		skipIf: () => !findFirstRowPrices(),
		icon: 'palette',
		title: ES ? 'Celdas de Comparación de Precios' : 'Price Comparison Cells',
		description: ES
			? 'Cada celda muestra el precio principal en grande y, debajo, el precio del líder con la diferencia en porcentaje. El borde rojo indica que el precio principal es más barato que el del líder, el azul que es más caro y el gris que están igualados. La flecha roja baja cuando es más barato y la verde sube cuando es más caro.'
			: 'Each cell shows the main price in large type and, under it, the leader\'s price with the difference as a percentage. A red border means the main price is cheaper than the leader\'s, blue that it is more expensive, and grey that they are equal. The red arrow points down when cheaper and the green one up when more expensive.',
		example: ES
			? 'Rojo: más barato ↓12% | Azul: más caro ↑8% | Gris: igualado'
			: 'Red: cheaper ↓12% | Blue: more expensive ↑8% | Grey: equal',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Selecciona tus filtros, pulsa «Search» y analiza tu posición competitiva. Usa el selector Mín./Media/Máx. para ver diferentes agregaciones de precios. Las celdas rojas indican dónde estás por debajo del líder — las azules, dónde estás por encima.'
			: 'Select your filters, hit "Search", and analyze your competitive position. Use the Min/Avg/Max toggle to see different price aggregations. Red cells are where you sit below the leader — blue cells where you sit above it.',
	},
]

export const TOUR_STEPS = STEPS.map(step => step.findTarget ? { ...step, findTarget: () => instant(step.findTarget()) } : step)
